import { useState, useEffect, useRef } from 'react'

interface TabOption {
  key: string
  label: string
}

interface LoginTabsProps {
  options: TabOption[]
  activeKey: string
  onChange: (key: string) => void
}

export const LoginTabs = ({ options, activeKey, onChange }: LoginTabsProps) => {
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 48 })
  const [isVisible, setIsVisible] = useState(false)
  const tabsRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const updateIndicatorPosition = () => {
    const activeIndex = options.findIndex((option) => option.key === activeKey)
    if (tabRefs.current[activeIndex]) {
      const activeTab = tabRefs.current[activeIndex]
      if (activeTab) {
        const tabRect = activeTab.getBoundingClientRect()
        setIndicatorStyle({
          left: activeTab.offsetLeft + (tabRect.width - 48) / 2,
          width: 48,
        })
        // 使用更平滑的显示方式，不阻塞渲染
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true)
          })
        })
      }
    }
  }

  useEffect(() => {
    // 当activeKey改变时，先隐藏指示器
    setIsVisible(false)

    // 立即更新位置
    updateIndicatorPosition()

    // 减少延迟时间，更快显示
    const timer = setTimeout(updateIndicatorPosition, 5)

    return () => clearTimeout(timer)
  }, [activeKey, options])

  // 组件挂载后立即更新位置
  useEffect(() => {
    // 使用更平滑的初始化
    requestAnimationFrame(() => {
      updateIndicatorPosition()
      // 备用更新，确保计算准确
      requestAnimationFrame(() => {
        updateIndicatorPosition()
      })
    })
  }, [])

  return (
    <div className="relative" ref={tabsRef}>
      <div className="flex items-center gap-[46px]">
        {options.map((option, index) => (
          <button
            key={option.key}
            ref={(el: HTMLButtonElement | null) => {
              tabRefs.current[index] = el
            }}
            className={`text-[16px] transition-colors duration-300 ${
              activeKey === option.key
                ? 'text-[#0B0B0B] font-medium'
                : 'text-[#86868B] font-normal'
            }`}
            onClick={() => onChange(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div
        className={`absolute -bottom-[10px] h-[6px] bg-gradient-to-r from-[#8752FF] via-[#8752FF] to-[#285AFF] rounded-[10px] transition-all duration-300 ease-out ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
          transformOrigin: 'center',
        }}
      />
    </div>
  )
}
